/**
 * TenantStats Component
 *
 * Displays tenant statistics in two rows of cards:
 * - Row 1: Status stats (Total, Active, Suspended, Not Provisioned)
 * - Row 2: Isolation level stats (Row-Level, Schema, Container)
 *
 * @module features/tenants/components/TenantStats
 */

import type { TenantStats as TenantStatsType } from '../../types';

interface TenantStatsProps {
  /** Tenant statistics from the API */
  stats: TenantStatsType | null;
  /** Count of tenants not yet provisioned */
  notProvisionedCount: number;
}

/**
 * Renders tenant statistics as card grids.
 */
export const TenantStats: React.FC<TenantStatsProps> = ({ stats, notProvisionedCount }) => {
  if (!stats) {
    return null;
  }

  return (
    <>
      {/* Status Stats Row */}
      <div className="stats-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '16px'
      }}>
        <div className="glass-card stat-card" style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="stat-label" style={{ margin: 0 }}>Total Tenants</div>
          <div className="stat-value" style={{ margin: 0 }}>{stats.total_tenants}</div>
        </div>
        <div className="glass-card stat-card" style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="stat-label" style={{ margin: 0 }}>Active</div>
          <div className="stat-value" style={{ color: 'var(--success)', margin: 0 }}>{stats.active_tenants}</div>
        </div>
        <div className="glass-card stat-card" style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="stat-label" style={{ margin: 0 }}>Suspended</div>
          <div className="stat-value" style={{ color: 'var(--warning)', margin: 0 }}>{stats.suspended_tenants}</div>
        </div>
        <div className="glass-card stat-card" style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="stat-label" style={{ margin: 0 }}>Not Provisioned</div>
          <div className="stat-value" style={{ color: 'var(--warning)', margin: 0 }}>{notProvisionedCount}</div>
        </div>
      </div>

      {/* Isolation Level Stats Row */}
      <div className="stats-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div className="glass-card stat-card" style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="stat-label" style={{ margin: 0 }}>Row-Level Isolation</div>
          <div className="stat-value" style={{ color: 'var(--text-secondary)', margin: 0 }}>
            {stats.by_isolation_level?.row || 0}
          </div>
        </div>
        <div className="glass-card stat-card" style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="stat-label" style={{ margin: 0 }}>Schema Isolation</div>
          <div className="stat-value" style={{ color: 'var(--primary)', margin: 0 }}>
            {stats.by_isolation_level?.schema || 0}
          </div>
        </div>
        <div className="glass-card stat-card" style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="stat-label" style={{ margin: 0 }}>Container Isolation</div>
          <div className="stat-value" style={{ color: 'var(--accent)', margin: 0 }}>
            {stats.by_isolation_level?.container || 0}
          </div>
        </div>
      </div>
    </>
  );
};
